import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Shield, Anchor, Briefcase, Users, Lock as LockIcon } from "lucide-react";
import { DISPLAY_DOMAIN } from "../lib/brand";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      // Role-based redirect (participant-console-002, PINNED): the external
      // `partner` principal's home is the participant-management console, not
      // the member dashboard. Everyone else lands on /dashboard as before.
      navigate(user.role === "partner" ? "/console" : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-4 mb-8">
          <img src="/logo.png" alt="EchoSense Capital Club" className="w-16 h-16 object-contain" />
          <div className="text-center">
            <span className="text-3xl font-semibold text-cream font-serif">EchoSense Capital Club</span>
            <span className="text-platinum/50 text-sm block">{DISPLAY_DOMAIN}</span>
          </div>
        </Link>

        <Card className="bg-white border-slate-200 shadow-xl animate-fade-in">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-serif text-slate-900">Welcome Back</CardTitle>
            <CardDescription className="text-slate-600">
              Sign in to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-slate-200 text-slate-900 placeholder:text-platinum/60"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white border-slate-200 text-slate-900 pr-10 placeholder:text-platinum/60"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-platinum/50 hover:text-platinum"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm font-medium"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Signing in..." : "Sign In"}
                {!loading && <ArrowRight className="ml-2" size={18} />}
              </Button>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-burgundy hover:text-burgundy-light font-medium"
                  data-testid="login-forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-slate-600">
                Don't have an account?{" "}
                <Link to="/register" className="text-burgundy hover:text-burgundy-light font-medium">
                  Create Account
                </Link>
              </p>
            </div>

            {/* Who We Are — institutional positioning */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-burgundy/30 text-burgundy hover:bg-burgundy/5 hover:text-burgundy font-medium"
                    data-testid="login-who-we-are-btn"
                  >
                    <Anchor className="w-4 h-4 mr-2" />
                    Who We Are
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl bg-white" data-testid="login-who-we-are-dialog">
                  <DialogHeader>
                    <p className="text-burgundy text-[10px] tracking-[0.35em] uppercase text-center mb-2">
                      EchoSense Capital Club
                    </p>
                    <DialogTitle className="text-2xl font-serif text-slate-900 text-center">
                      The Private Capital Member Portal
                    </DialogTitle>
                    <DialogDescription className="text-slate-600 text-center pt-2 leading-relaxed">
                      A confidential, invitation-only platform connecting verified
                      high-net-worth individuals with curated capital opportunities
                      through our regulated asset-management partners.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                      <Shield className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-900 text-sm font-semibold">Introducer Mandate</p>
                        <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                          We act strictly as an introducer and verification gateway —
                          never as an investment adviser.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                      <LockIcon className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-900 text-sm font-semibold">Compliance-First</p>
                        <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                          KYC, AML, and accreditation are completed inside the portal
                          before any partner introduction is made.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                      <Briefcase className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-900 text-sm font-semibold">Curated Deal Room</p>
                        <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                          Opportunities are visibility-tiered (Standard, Institutional,
                          HNW, Sovereign) and unlocked only after partner funding.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
                      <Users className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-slate-900 text-sm font-semibold">Member-Only</p>
                        <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                          Membership begins with a one-time registration fee and a
                          private intake with our desk.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-4 rounded-lg bg-burgundy/5 border border-burgundy/15">
                    <p className="text-slate-700 text-xs leading-relaxed text-center">
                      EchoSense Capital Markets acts solely as an Introducer.
                      No solicitation, no investment advice, and no offer of
                      securities is intended or implied through this portal.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <Link to="/register" className="flex-1">
                      <Button className="w-full bg-burgundy hover:bg-burgundy-light text-white" data-testid="who-we-are-register">
                        Create Account
                      </Button>
                    </Link>
                    <Link to="/about" className="flex-1">
                      <Button variant="outline" className="w-full" data-testid="who-we-are-learn-more">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
